import * as types from './salesMatching.types';
import {
  IaddToSalesMatchingToUpdateBody,
  IInitialSalesMatchingState,
  IsalesMatchingOptions,
  IsalesMatchingToUpdateItem,
} from 'interfaces/salesMatching.interface';
import { Dispatch } from 'react';
import {
  IbrandCatalogFilterAPIBody,
  IbrandCatalogFilterAPIResponse,
  IbrandCatalogFilterAPIResponseProductItem,
  IgetSalesMatchingAPIBody,
  IgetSalesMatchingData,
  IgetSalesMatchingDataItem,
  ImatchSaleAPIBodyItem,
  IsetOwnClientProductAPIBodyItem,
  IunmatchSaleAPIBody,
} from 'api/endpoints/salesMatching/salesMatching.response.interface';
import {
  brandCatalogFilterAPI,
  getSalesMatchingItemAPI,
  matchSaleAPI,
  setOwnClientProductAPI,
  unmatchSaleAPI,
} from 'api/endpoints/salesMatching/salesMatching.api';
import { notificationErr } from 'views/components/UI/notification';
import { salesMatchingError } from 'utils/errorCodes';
import { ITranslate } from 'interfaces/general.interface';
import {
  messageError,
  messageSuccess,
  messageWarning,
} from 'views/components/UI/message';
import { IuseTableColumnSearchV2PropsSearchItems } from 'hooks/useTableColumnSearchV2';
import { DropResult } from 'react-beautiful-dnd';
import { ITableColumnsSelectOrderState } from 'interfaces/tables.interface';
import { isEqual } from 'lodash';
import { TsalesMatchingDispatchReducer } from './salesMatching.reducer';

export async function getSalesMatchingItemsAction({
  dispatch,
  salesMatchingBodyFetch,
  clientId,
  signal,
  token,
  translate,
}: {
  dispatch: React.Dispatch<TsalesMatchingDispatchReducer>;
  salesMatchingBodyFetch: IgetSalesMatchingAPIBody;
  clientId: string;
  signal: AbortSignal;
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  salesMatchingBodyFetch.filter.client_id.value = clientId;
  salesMatchingBodyFetch.filter.matched = {
    value: false,
    operator: '=',
  };
  salesMatchingBodyFetch.filter.own = {
    value: false,
    operator: '=',
  };
  const getCatalog = await getCatalogData({
    dispatch,
    salesMatchingBodyFetch,
    signal,
    token,
  });
  if (!getCatalog) {
    notificationErr(salesMatchingError.getSalesMatchingAction_001, translate);
    return false;
  }
  return true;
}

export function setSalesMatchingItemsSelectedAction({
  dispatch,
  items,
}: {
  dispatch: React.Dispatch<TsalesMatchingDispatchReducer>;
  items: Array<IgetSalesMatchingDataItem>;
}) {
  dispatch({
    type: types.SET_SALES_MATCHING_ITEMS_SELECTED,
    payload: items,
  });
}

export async function searchOnCatalogItemsAction({
  dispatch,
  catalogBodyFetch,
  searchOnCatalogItemsProps,
  signal,
  token,
  translate,
}: {
  dispatch: React.Dispatch<TsalesMatchingDispatchReducer>;
  catalogBodyFetch: IgetSalesMatchingAPIBody;
  searchOnCatalogItemsProps: IuseTableColumnSearchV2PropsSearchItems;
  signal: AbortSignal;
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  if (searchOnCatalogItemsProps.type === 'add') {
    if (searchOnCatalogItemsProps.searchType === '%ILIKE%') {
      catalogBodyFetch.filter = {
        ...catalogBodyFetch.filter,
        [searchOnCatalogItemsProps.searchField]: {
          value: `%${searchOnCatalogItemsProps.searchText}%`,
          operator: 'ILIKE',
        },
      };
    } else if (searchOnCatalogItemsProps.searchType === '%ILIKE') {
      catalogBodyFetch.filter = {
        ...catalogBodyFetch.filter,
        [searchOnCatalogItemsProps.searchField]: {
          value: `%${searchOnCatalogItemsProps.searchText}`,
          operator: 'ILIKE',
        },
      };
    } else if (searchOnCatalogItemsProps.searchType === 'ILIKE%') {
      catalogBodyFetch.filter = {
        ...catalogBodyFetch.filter,
        [searchOnCatalogItemsProps.searchField]: {
          value: `${searchOnCatalogItemsProps.searchText}%`,
          operator: 'ILIKE',
        },
      };
    } else if (searchOnCatalogItemsProps.searchType === 'IS NULL') {
      catalogBodyFetch.filter = {
        ...catalogBodyFetch.filter,
        [searchOnCatalogItemsProps.searchField]: {
          value: null,
          operator: 'IS NULL',
        },
      };
    } else if (searchOnCatalogItemsProps.searchType === 'IS NOT NULL') {
      catalogBodyFetch.filter = {
        ...catalogBodyFetch.filter,
        [searchOnCatalogItemsProps.searchField]: {
          value: null,
          operator: 'IS NOT NULL',
        },
      };
    } else if (searchOnCatalogItemsProps.searchType === 'IN') {
      catalogBodyFetch.filter = {
        ...catalogBodyFetch.filter,
        [searchOnCatalogItemsProps.searchField]: {
          value: searchOnCatalogItemsProps.searchText,
          operator: 'IN',
        },
      };
    } else if (searchOnCatalogItemsProps.searchType === 'NOT IN') {
      catalogBodyFetch.filter = {
        ...catalogBodyFetch.filter,
        [searchOnCatalogItemsProps.searchField]: {
          value: searchOnCatalogItemsProps.searchText,
          operator: 'NOT IN',
        },
      };
    } else {
      catalogBodyFetch.filter = {
        ...catalogBodyFetch.filter,
        [searchOnCatalogItemsProps.searchField]: {
          value: searchOnCatalogItemsProps.searchText,
          operator: searchOnCatalogItemsProps.searchType,
        },
      };
    }
  } else {
    delete catalogBodyFetch.filter[searchOnCatalogItemsProps.searchField];
  }
  const getCatalog = await getCatalogData({
    dispatch,
    salesMatchingBodyFetch: catalogBodyFetch,
    signal,
    token,
  });

  if (!getCatalog) {
    notificationErr(salesMatchingError.searchOnCatalogItemsAction_001, translate);
    return false;
  }
  return true;
}

export async function setSortByAction({
  dispatch,
  salesMatchingBodyFetch,
  type,
  value,
  signal,
  token,
  translate,
}: {
  dispatch: Dispatch<TsalesMatchingDispatchReducer>;
  salesMatchingBodyFetch: IgetSalesMatchingAPIBody;
  type: 'matched' | 'own';
  value: boolean;
  signal: AbortSignal;
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  if (type === 'matched') {
    salesMatchingBodyFetch.filter.matched = {
      value: value,
      operator: '=',
    };
    salesMatchingBodyFetch.filter.own = {
      value: false,
      operator: '=',
    };
  }
  if (type === 'own') {
    delete salesMatchingBodyFetch.filter.matched;
    salesMatchingBodyFetch.filter.own = {
      value: value,
      operator: '=',
    };
  }
  const getCatalog = await getCatalogData({
    dispatch,
    salesMatchingBodyFetch,
    signal,
    token,
  });
  if (!getCatalog) {
    notificationErr(salesMatchingError.getSalesMatchingAction_001, translate);
    return false;
  }
  return true;
}

export async function addToSalesMatchingToUpdateAction({
  dispatch,
  body,
  salesMatchingToUpdate,
  options,
  token,
  translate,
}: {
  dispatch: Dispatch<TsalesMatchingDispatchReducer>;
  body: IaddToSalesMatchingToUpdateBody;
  salesMatchingToUpdate: Array<IsalesMatchingToUpdateItem>;
  options: Array<IsalesMatchingOptions>;
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  dispatch({
    type: types.SET_IS_LOADING_ADD_TO_SALES_MATCHING_TO_UPDATE,
    payload: {
      saleId: body.sales_id,
      isLoading: true,
    },
  });

  try {
    const findItem =
      salesMatchingToUpdate.find((i) => i.sales_id === body.sales_id) ?? null;

    let bodyFetch: IbrandCatalogFilterAPIBody = {
      brand_id: '',
    };

    if (body.type === 'brand') bodyFetch.brand_id = body.brand_id;
    if (body.type === 'product_description')
      bodyFetch.product_description = body.product_description;
    if (body.type === 'reference') bodyFetch.reference_id = body.reference_id;
    if (body.type === 'color') bodyFetch.color_id = body.color_id;
    if (body.type === 'size') bodyFetch.size_id = body.size;
    if (body.type === 'sku') bodyFetch.sku_id = body.sku;

    if (!findItem) {
      let item: IsalesMatchingToUpdateItem = {
        sales_id: body.sales_id,
        brand_id: bodyFetch.brand_id ?? '',
        reference_id: bodyFetch.reference_id ?? null,
        product_description: bodyFetch.product_description ?? null,
        color_id: bodyFetch.color_id ?? null,
        size: bodyFetch.size_id ?? null,
        sku: bodyFetch.sku_id ?? null,
        options: {
          reference: [],
          product_description: [],
          color: [],
          size: [],
          sku: [],
          product: [],
          images: [],
        },
      };

      const findBrandOptions = options.find(
        (option) => option.brandId === bodyFetch.brand_id
      );

      if (findBrandOptions) {
        item.options = {
          reference: findBrandOptions.options.references ?? [],
          product_description: findBrandOptions.options.product_description ?? [],
          color: findBrandOptions.options.colors ?? [],
          size: findBrandOptions.options.sizes ?? [],
          sku: findBrandOptions.options.skus ?? [],
          product: findBrandOptions.options.products ?? [],
          images: [],
        };
        dispatch({
          type: types.ADD_TO_SALES_MATCHING_TO_UPDATE,
          payload: [...salesMatchingToUpdate, item],
        });
        return true;
      }

      let getOptions: IbrandCatalogFilterAPIResponse;

      getOptions = await brandCatalogFilterAPI({
        body: bodyFetch,
        token: token,
      });
      if (getOptions.response.status !== 200 && getOptions.response.status !== 404) {
        notificationErr(
          salesMatchingError.addToSalesMatchingToUpdateReferenceAction_002,
          translate
        );
        return false;
      }

      if (getOptions.data.references && getOptions.data.references.length === 1) {
        item.reference_id = getOptions.data.references[0].id;
        bodyFetch.reference_id = getOptions.data.references[0].id;
      }
      if (
        getOptions.data.product_description &&
        getOptions.data.product_description.length === 1
      ) {
        item.product_description = getOptions.data.product_description[0].id;
        bodyFetch.product_description = getOptions.data.product_description[0].id;
      }
      if (getOptions.data.colors && getOptions.data.colors.length === 1) {
        item.color_id = getOptions.data.colors[0].id;
        bodyFetch.color_id = getOptions.data.colors[0].id;
      }
      if (getOptions.data.sizes && getOptions.data.sizes.length === 1) {
        item.size = getOptions.data.sizes[0].id;
        bodyFetch.size_id = getOptions.data.sizes[0].id;
      }
      if (getOptions.data.skus && getOptions.data.skus.length === 1) {
        item.sku = getOptions.data.skus[0].id;
        bodyFetch.sku_id = getOptions.data.skus[0].id;
      }

      dispatch({
        type: types.SET_SALES_MATCHING_TO_UPDATE_BODY,
        payload: bodyFetch,
      });

      item.options.reference = getOptions.data.references ?? [];
      item.options.product_description = getOptions.data.product_description ?? [];
      item.options.color = getOptions.data.colors ?? [];
      item.options.size = getOptions.data.sizes ?? [];
      item.options.sku = getOptions.data.skus ?? [];
      item.options.product = getOptions.data.products ?? [];
      item.options.images = getOptions.data.images ?? [];

      if (!findBrandOptions && bodyFetch.brand_id) {
        const brandItem: IsalesMatchingOptions = {
          brandId: bodyFetch.brand_id,
          options: {
            references: getOptions.data.references ?? [],
            product_description: getOptions.data.product_description ?? [],
            colors: getOptions.data.colors ?? [],
            sizes: getOptions.data.sizes ?? [],
            skus: getOptions.data.skus ?? [],
            products: getOptions.data.products ?? [],
          },
        };
        const payload: IsalesMatchingOptions[] = [...options, brandItem];
        dispatch({
          type: types.SET_OPTIONS,
          payload: payload,
        });
      }

      dispatch({
        type: types.ADD_TO_SALES_MATCHING_TO_UPDATE,
        payload: [...salesMatchingToUpdate, item],
      });
      return true;
    }

    if (findItem?.brand_id) bodyFetch.brand_id = findItem.brand_id;

    if (body.type === 'reference' && body.reference_id === undefined) {
      delete bodyFetch.reference_id;
      delete bodyFetch.product_description;
      delete bodyFetch.color_id;
      delete bodyFetch.size_id;
      delete bodyFetch.sku_id;
      findItem.reference_id = null;
      findItem.product_description = null;
      findItem.color_id = null;
      findItem.size = null;
      findItem.sku = null;
    }

    if (body.type === 'product_description' && body.product_description === undefined) {
      delete bodyFetch.product_description;
      delete bodyFetch.color_id;
      delete bodyFetch.size_id;
      delete bodyFetch.sku_id;
      findItem.product_description = null;
      findItem.color_id = null;
      findItem.size = null;
      findItem.sku = null;
      bodyFetch.reference_id = findItem.reference_id ?? undefined;
    }

    if (body.type === 'color' && body.color_id === undefined) {
      delete bodyFetch.color_id;
      delete bodyFetch.size_id;
      delete bodyFetch.sku_id;
      findItem.color_id = null;
      findItem.size = null;
      findItem.sku = null;
      bodyFetch.reference_id = findItem.reference_id ?? undefined;
      bodyFetch.product_description = findItem.product_description ?? undefined;
    }

    if (body.type === 'size' && body.size === undefined) {
      delete bodyFetch.size_id;
      delete bodyFetch.sku_id;
      findItem.size = null;
      findItem.sku = null;
      bodyFetch.reference_id = findItem.reference_id ?? undefined;
      bodyFetch.product_description = findItem.product_description ?? undefined;
      bodyFetch.color_id = findItem.color_id ?? undefined;
    }

    if (body.type === 'sku' && body.sku === undefined) {
      delete bodyFetch.sku_id;
      findItem.sku = null;
      bodyFetch.reference_id = findItem.reference_id ?? undefined;
      bodyFetch.product_description = findItem.product_description ?? undefined;
      bodyFetch.color_id = findItem.color_id ?? undefined;
      bodyFetch.size_id = findItem.size ?? undefined;
    }

    if (body.type === 'brand' && body.brand_id) {
      bodyFetch.brand_id = body.brand_id;
      findItem.brand_id = body.brand_id ?? null;
      delete bodyFetch.reference_id;
      delete bodyFetch.product_description;
      delete bodyFetch.color_id;
      delete bodyFetch.size_id;
      delete bodyFetch.sku_id;
      findItem.reference_id = null;
      findItem.product_description = null;
      findItem.color_id = null;
      findItem.size = null;
      findItem.sku = null;
    }

    if (body.type === 'reference' && body.reference_id) {
      bodyFetch.reference_id = body.reference_id;
      findItem.reference_id = body.reference_id ?? null;
      delete bodyFetch.product_description;
      delete bodyFetch.color_id;
      delete bodyFetch.size_id;
      delete bodyFetch.sku_id;
      findItem.product_description = null;
      findItem.color_id = null;
      findItem.size = null;
      findItem.sku = null;
    }

    if (body.type === 'product_description' && body.product_description) {
      bodyFetch.reference_id = findItem.reference_id ?? undefined;
      bodyFetch.product_description = body.product_description;
      findItem.reference_id = findItem.reference_id ?? null;
      findItem.product_description = body.product_description ?? null;
      delete bodyFetch.color_id;
      delete bodyFetch.size_id;
      delete bodyFetch.sku_id;
      findItem.color_id = null;
      findItem.size = null;
      findItem.sku = null;
    }

    if (body.type === 'color' && body.color_id) {
      bodyFetch.reference_id = findItem.reference_id ?? undefined;
      bodyFetch.product_description = findItem.product_description ?? undefined;
      bodyFetch.color_id = body.color_id;
      findItem.reference_id = findItem.reference_id ?? null;
      findItem.product_description = findItem.product_description ?? null;
      findItem.color_id = body.color_id ?? null;
      delete bodyFetch.size_id;
      delete bodyFetch.sku_id;
      findItem.size = null;
      findItem.sku = null;
    }

    if (body.type === 'size' && body.size) {
      bodyFetch.reference_id = findItem.reference_id ?? undefined;
      bodyFetch.product_description = findItem.product_description ?? undefined;
      bodyFetch.color_id = findItem.color_id ?? undefined;
      bodyFetch.size_id = body.size;
      findItem.reference_id = findItem.reference_id ?? null;
      findItem.product_description = findItem.product_description ?? null;
      findItem.color_id = findItem.color_id ?? null;
      findItem.size = body.size ?? null;
      delete bodyFetch.sku_id;
      findItem.sku = null;
    }

    if (body.type === 'sku' && body.sku) {
      bodyFetch.reference_id = findItem.reference_id ?? undefined;
      bodyFetch.product_description = findItem.product_description ?? undefined;
      bodyFetch.color_id = findItem.color_id ?? undefined;
      bodyFetch.size_id = findItem.size ?? undefined;
      bodyFetch.sku_id = body.sku;
      findItem.reference_id = findItem.reference_id ?? null;
      findItem.product_description = findItem.product_description ?? null;
      findItem.color_id = findItem.color_id ?? null;
      findItem.size = findItem.size ?? null;
      findItem.sku = body.sku ?? null;
    }

    if (
      bodyFetch.brand_id &&
      !bodyFetch.reference_id &&
      !bodyFetch.product_description &&
      !bodyFetch.color_id &&
      !bodyFetch.size_id &&
      !bodyFetch.sku_id
    ) {
      const findBrandOptions = options.find(
        (option) => option.brandId === bodyFetch.brand_id
      );
      if (findBrandOptions) {
        findItem.options = {
          reference: findBrandOptions.options.references ?? [],
          product_description: findBrandOptions.options.product_description ?? [],
          color: findBrandOptions.options.colors ?? [],
          size: findBrandOptions.options.sizes ?? [],
          sku: findBrandOptions.options.skus ?? [],
          product: findBrandOptions.options.products ?? [],
          images: [],
        };
        dispatch({
          type: types.ADD_TO_SALES_MATCHING_TO_UPDATE,
          payload: salesMatchingToUpdate.map((i) =>
            i.sales_id === findItem?.sales_id ? findItem : i
          ),
        });
        return true;
      }
    }

    const copyOfBodyFetch = JSON.parse(JSON.stringify(bodyFetch));

    let getOptions: IbrandCatalogFilterAPIResponse;

    getOptions = await brandCatalogFilterAPI({
      body: copyOfBodyFetch,
      token: token,
    });

    if (getOptions.data.references && getOptions.data.references.length === 1) {
      findItem.reference_id = getOptions.data.references[0].id;
      copyOfBodyFetch.reference_id = getOptions.data.references[0].id;
    }
    if (
      getOptions.data.product_description &&
      getOptions.data.product_description.length === 1
    ) {
      findItem.product_description = getOptions.data.product_description[0].id;
      copyOfBodyFetch.product_description = getOptions.data.product_description[0].id;
    }
    if (getOptions.data.colors && getOptions.data.colors.length === 1) {
      findItem.color_id = getOptions.data.colors[0].id;
      copyOfBodyFetch.color_id = getOptions.data.colors[0].id;
    }
    if (getOptions.data.sizes && getOptions.data.sizes.length === 1) {
      findItem.size = getOptions.data.sizes[0].id;
      copyOfBodyFetch.size_id = getOptions.data.sizes[0].id;
    }
    if (getOptions.data.skus && getOptions.data.skus.length === 1) {
      findItem.sku = getOptions.data.skus[0].id;
      copyOfBodyFetch.sku_id = getOptions.data.skus[0].id;
    }

    dispatch({
      type: types.SET_SALES_MATCHING_TO_UPDATE_BODY,
      payload: copyOfBodyFetch,
    });

    if (!isEqual(bodyFetch, copyOfBodyFetch)) {
      getOptions = await brandCatalogFilterAPI({
        body: copyOfBodyFetch,
        token: token,
      });
      if (getOptions.response.status !== 200 && getOptions.response.status !== 404) {
        notificationErr(
          salesMatchingError.addToSalesMatchingToUpdateReferenceAction_002,
          translate
        );
        return false;
      }
    }

    if (body.type === 'brand' && body.brand_id) findItem.brand_id = body.brand_id;
    if (body.type === 'reference') findItem.reference_id = body.reference_id;
    if (body.type === 'product_description')
      findItem.product_description = body.product_description;
    if (body.type === 'color') findItem.color_id = body.color_id;
    if (body.type === 'size') findItem.size = body.size;
    if (body.type === 'sku') findItem.sku = body.sku;
    findItem.options.reference = getOptions.data.references ?? [];
    findItem.options.product_description = getOptions.data.product_description ?? [];
    findItem.options.color = getOptions.data.colors ?? [];
    findItem.options.size = getOptions.data.sizes ?? [];
    findItem.options.sku = getOptions.data.skus ?? [];
    findItem.options.product = getOptions.data.products ?? [];
    findItem.options.images = getOptions.data.images ?? [];

    if (body.type === 'reference' && body.reference_id === undefined) {
      findItem.reference_id = null;
      findItem.product_description = null;
      findItem.color_id = null;
      findItem.size = null;
      findItem.sku = null;
    }

    if (body.type === 'product_description' && body.product_description === undefined) {
      findItem.product_description = null;
      findItem.color_id = null;
      findItem.size = null;
      findItem.sku = null;
    }

    if (body.type === 'color' && body.color_id === undefined) {
      findItem.color_id = null;
      findItem.size = null;
      findItem.sku = null;
    }

    if (body.type === 'size' && body.size === undefined) {
      findItem.size = null;
      findItem.sku = null;
    }

    if (body.type === 'sku' && body.sku === undefined) {
      findItem.sku = null;
    }

    dispatch({
      type: types.ADD_TO_SALES_MATCHING_TO_UPDATE,
      payload: salesMatchingToUpdate.map((i) =>
        i.sales_id === findItem?.sales_id ? findItem : i
      ),
    });
    return true;
  } catch (error) {
    notificationErr(
      salesMatchingError.addToSalesMatchingToUpdateReferenceAction_003,
      translate
    );
    return false;
  } finally {
    dispatch({
      type: types.SET_IS_LOADING_ADD_TO_SALES_MATCHING_TO_UPDATE,
      payload: {
        saleId: null,
        isLoading: false,
      },
    });
  }
}

export async function matchSaleAction({
  dispatch,
  brand_product,
  catalog_sale,
  salesMatchingBodyFetch,
  salesMatchingItems,
  salesMatchingToUpdateBody,
  signal,
  token,
  translate,
}: {
  dispatch: Dispatch<TsalesMatchingDispatchReducer>;
  brand_product: IbrandCatalogFilterAPIResponseProductItem;
  catalog_sale: IgetSalesMatchingDataItem;
  salesMatchingBodyFetch: IgetSalesMatchingAPIBody;
  salesMatchingItems: IgetSalesMatchingData;
  salesMatchingToUpdateBody: IbrandCatalogFilterAPIBody | null;
  signal: AbortSignal;
  token: string;
  translate: ITranslate;
}): Promise<{
  success: boolean;
  similarItemsBody?: ImatchSaleAPIBodyItem[];
}> {
  delete catalog_sale.key;
  delete catalog_sale.own;

  const body: ImatchSaleAPIBodyItem[] = [
    {
      ticket_id: catalog_sale.id,
      sku_id: brand_product.sku_id,
      client_product_id: catalog_sale.client_product_id,
    },
  ];
  try {
    const matchSaleFetch = await matchSaleAPI(body, token);
    if (matchSaleFetch.response.status === 403) {
      messageWarning(translate('sales-matching_match-sale_premission-error'));
    }

    if (matchSaleFetch.response.status === 200) {
      const getCatalog = await getCatalogData({
        dispatch,
        salesMatchingBodyFetch,
        signal,
        token,
      });
      if (!getCatalog) {
        messageError(translate('sales-matching_match-sale_error'));
        notificationErr(salesMatchingError.matchSaleAction_003, translate);
        return {
          success: false,
        };
      }
      messageSuccess(translate('sales-matching_match-sale_success'));
      try {
        const similarItems: IgetSalesMatchingDataItem[] = [];
        const similarItemsBody: ImatchSaleAPIBodyItem[] = [];

        for (const item of salesMatchingItems.data) {
          if (
            item.reference === catalog_sale.reference &&
            // item.product_description === catalog_sale.product_description &&
            item.color === catalog_sale.color &&
            item.color_code === catalog_sale.color_code &&
            item.id !== catalog_sale.id
          ) {
            similarItems.push(item);
          }
        }

        delete salesMatchingToUpdateBody?.sku_id;
        delete salesMatchingToUpdateBody?.size_id;
        const getOptions = await brandCatalogFilterAPI({
          body: salesMatchingToUpdateBody!,
          token: token,
        });
        if (
          getOptions.response.status === 200 &&
          getOptions.data.products &&
          getOptions.data.products.length > 0
        ) {
          for (const item of similarItems) {
            const findProduct = getOptions.data.products.find((product) => {
              const itemSize = item.size?.startsWith('00')
                ? item.size.slice(2)
                : item.size;
              const productSize = product.size?.startsWith('00')
                ? product.size.slice(2)
                : product.size;
              return productSize === itemSize;
            });
            if (findProduct) {
              delete item.key;
              delete item.own;
              similarItemsBody.push({
                ticket_id: item.id,
                sku_id: findProduct.sku_id,
                client_product_id: item.client_product_id,
                product_description: findProduct.product_description,
                color: findProduct.color,
                size: findProduct.size,
                ean: findProduct.ean,
              });
            }
          }
          return {
            success: true,
            similarItemsBody: similarItemsBody.length > 0 ? similarItemsBody : undefined,
          };
        }
        return {
          success: false,
        };
      } catch (error) {
        console.log(error);
        notificationErr(salesMatchingError.matchSaleAction_004, translate);
        return {
          success: false,
        };
      }
    } else {
      messageError(translate('sales-matching_match-sale_error'));
      notificationErr(salesMatchingError.matchSaleAction_001, translate);
      return {
        success: false,
      };
    }
  } catch (err) {
    messageError(translate('sales-matching_match-sale_error'));
    notificationErr(salesMatchingError.matchSaleAction_002, translate);
    return {
      success: false,
    };
  }
}

export async function matchSaleSimilarItemsAction({
  dispatch,
  salesMatchingBodyFetch,
  similarItemsBody,
  signal,
  token,
  translate,
}: {
  dispatch: Dispatch<TsalesMatchingDispatchReducer>;
  salesMatchingBodyFetch: IgetSalesMatchingAPIBody;
  similarItemsBody: ImatchSaleAPIBodyItem[];
  signal: AbortSignal;
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  try {
    for (const item of similarItemsBody) {
      delete item.color;
      delete item.size;
      delete item.ean;
      delete item.product_description;
    }
    const matchSaleFetch = await matchSaleAPI(similarItemsBody, token);
    if (matchSaleFetch.response.status === 403) {
      messageWarning(translate('sales-matching_match-sale_premission-error'));
      return false;
    }

    if (matchSaleFetch.response.status === 404) {
      messageWarning(
        translate('sales-matching_match-sale_client-product-id-not-found-error')
      );
      return false;
    }

    if (matchSaleFetch.response.status === 200) {
      salesMatchingBodyFetch.index = 0;
      await getCatalogData({
        dispatch,
        salesMatchingBodyFetch,
        signal,
        token,
      });
      // if (!getCatalog) {
      //   messageError(translate('sales-matching_match-sale_error'));
      //   notificationErr(salesMatchingError.matchSaleActionSimilarItems_003, translate);
      //   return false;
      // }
      messageSuccess(translate('sales-matching_match-sale_success'));
      return true;
    } else {
      messageError(translate('sales-matching_match-sale_error'));
      notificationErr(salesMatchingError.matchSaleActionSimilarItems_001, translate);
      return false;
    }
  } catch (err) {
    messageError(translate('sales-matching_match-sale_error'));
    notificationErr(salesMatchingError.matchSaleActionSimilarItems_002, translate);
    return false;
  }
}

export async function cancelMatchSaleAction({
  dispatch,
  sale_id,
  salesMatchingToUpdate,
}: {
  dispatch: Dispatch<TsalesMatchingDispatchReducer>;
  sale_id: string;
  salesMatchingToUpdate: Array<IsalesMatchingToUpdateItem>;
}) {
  const findItem = salesMatchingToUpdate.find((i) => i.sales_id === sale_id);
  if (!findItem) return false;

  return dispatch({
    type: types.ADD_TO_SALES_MATCHING_TO_UPDATE,
    payload: salesMatchingToUpdate.filter((i) => i.sales_id !== findItem.sales_id),
  });
}

export async function unmatchSalesAction({
  dispatch,
  salesMatchingItemsSelected,
  salesMatchingBodyFetch,
  signal,
  token,
  translate,
}: {
  dispatch: Dispatch<TsalesMatchingDispatchReducer>;
  salesMatchingItemsSelected: Array<IgetSalesMatchingDataItem>;
  salesMatchingBodyFetch: IgetSalesMatchingAPIBody;
  signal: AbortSignal;
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  dispatch({ type: types.SET_IS_LOADING_UNMATCH_SALES, payload: true });
  try {
    if (salesMatchingItemsSelected.length === 0) {
      messageWarning(translate('sales-matching_unlink-sale_no-selection'));
      return false;
    }
    const body: Array<IunmatchSaleAPIBody> = salesMatchingItemsSelected.map((item) => {
      return {
        ticket_id: item.id,
        client_product_id: item.client_product_id,
      };
    });
    const unmatchSaleFetch = await unmatchSaleAPI(body, token);
    if (unmatchSaleFetch.response.status === 200) {
      messageSuccess(translate('sales-matching_unlink-sale_success'));
      dispatch({
        type: types.SET_SALES_MATCHING_ITEMS_SELECTED,
        payload: [],
      });
      const getCatalog = await getCatalogData({
        dispatch,
        salesMatchingBodyFetch,
        signal,
        token,
      });
      if (!getCatalog) {
        notificationErr(salesMatchingError.unmatchSalesAction_003, translate);
        return false;
      }
      return true;
    } else {
      messageError(translate('sales-matching_unlink-sale_error'));
      notificationErr(salesMatchingError.unmatchSalesAction_001, translate);
      return false;
    }
  } catch (err) {
    messageError(translate('sales-matching_unlink-sale_error'));
    notificationErr(salesMatchingError.unmatchSalesAction_002, translate);
    return false;
  } finally {
    dispatch({ type: types.SET_IS_LOADING_UNMATCH_SALES, payload: false });
  }
}

export async function handlePaginationAction({
  dispatch,
  page,
  pageSize,
  salesMatchingState,
  signal,
  token,
  translate,
}: {
  dispatch: Dispatch<TsalesMatchingDispatchReducer>;
  page: number;
  pageSize: number;
  salesMatchingState: IInitialSalesMatchingState;
  signal: AbortSignal;
  token: string;
  translate: ITranslate;
}) {
  salesMatchingState.salesMatchingBodyFetch.index =
    page === 1 ? 0 : page * pageSize - pageSize;
  salesMatchingState.salesMatchingBodyFetch.limit = pageSize;
  const getCatalog = await getCatalogData({
    dispatch,
    salesMatchingBodyFetch: salesMatchingState.salesMatchingBodyFetch,
    signal,
    token,
  });
  if (!getCatalog) {
    notificationErr(salesMatchingError.handlePaginationAction_001, translate);
    return false;
  }
  return true;
}

export async function setOwnClientProductAction({
  dispatch,
  salesMatchingBodyFetch,
  isOwn,
  salesMatchingItemsSelected,
  signal,
  token,
  translate,
}: {
  dispatch: Dispatch<TsalesMatchingDispatchReducer>;
  salesMatchingBodyFetch: IgetSalesMatchingAPIBody;
  isOwn: boolean;
  salesMatchingItemsSelected: Array<IgetSalesMatchingDataItem>;
  signal: AbortSignal;
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  dispatch({ type: types.SET_IS_LOADING_OWN_CLIENT_PRODUCT, payload: true });
  try {
    if (salesMatchingItemsSelected.length === 0) {
      messageWarning(translate('sales-matching_set-own-client-product_no-selection'));
      return false;
    }
    const body: Array<IsetOwnClientProductAPIBodyItem> = salesMatchingItemsSelected
      .filter((item) => item.own === !isOwn)
      .map((item) => {
        return {
          ticket_id: item.id,
          client_product_id: item.client_product_id,
          own: isOwn,
        };
      });
    const setOwnClientProductFetch = await setOwnClientProductAPI(body, token);
    if (setOwnClientProductFetch.response.status === 400) {
      if (
        setOwnClientProductFetch.data.updated.length === 0 &&
        setOwnClientProductFetch.data.error.length > 0
      ) {
        messageWarning(translate('sales-matching_set-own-client-product_update-error'));
      } else if (
        setOwnClientProductFetch.data.updated.length > 0 &&
        setOwnClientProductFetch.data.error.length > 0
      ) {
        messageWarning(
          `${translate('sales-matching_set-own-client-product_update-warning-1')} ${setOwnClientProductFetch.data.updated.length}. ${translate('sales-matching_set-own-client-product_update-warning-2')} ${setOwnClientProductFetch.data.error.length}`
        );
      } else if (
        setOwnClientProductFetch.data.updated.length > 0 &&
        setOwnClientProductFetch.data.error.length === 0
      ) {
        messageSuccess(translate('sales-matching_set-own-client-product_success'));
      }
      dispatch({
        type: types.SET_SALES_MATCHING_ITEMS_SELECTED,
        payload: [],
      });
      const getCatalog = await getCatalogData({
        dispatch,
        salesMatchingBodyFetch,
        signal,
        token,
      });
      if (!getCatalog) {
        notificationErr(salesMatchingError.handlePaginationAction_001, translate);
        return false;
      }
      return true;
    }
    if (setOwnClientProductFetch.response.status === 200) {
      messageSuccess(translate('sales-matching_set-own-client-product_success'));
      dispatch({
        type: types.SET_SALES_MATCHING_ITEMS_SELECTED,
        payload: [],
      });
      const getCatalog = await getCatalogData({
        dispatch,
        salesMatchingBodyFetch,
        signal,
        token,
      });
      if (!getCatalog) {
        notificationErr(salesMatchingError.handlePaginationAction_001, translate);
        return false;
      }
      return true;
    } else {
      messageError(translate('sales-matching_set-own-client-product_error'));
      notificationErr(salesMatchingError.setOwnClientProductAction_001, translate);
      return false;
    }
  } catch (err) {
    messageError(translate('sales-matching_set-own-client-product_error'));
    notificationErr(salesMatchingError.setOwnClientProductAction_002, translate);
    return false;
  } finally {
    dispatch({ type: types.SET_IS_LOADING_OWN_CLIENT_PRODUCT, payload: false });
  }
}

// COLUMNS ORDER /////////////////////////////////
export function moveColumnsOrderAction({
  dispatch,
  result,
  columnsOrder,
}: {
  dispatch: React.Dispatch<TsalesMatchingDispatchReducer>;
  result: DropResult;
  columnsOrder: Record<string, ITableColumnsSelectOrderState>;
}) {
  if (!result.destination) return;
  const items = Object.entries(columnsOrder);
  const [reorderedItem] = items.splice(result.source.index, 1);
  items.splice(result.destination.index, 0, reorderedItem);
  const updatedItems = items.map(([key, column], index) => {
    return [key, { ...column, order: index }];
  });
  const pd: Record<string, ITableColumnsSelectOrderState> =
    Object.fromEntries(updatedItems);
  dispatch({ type: types.SET_COLUMNS_ORDER, payload: pd });
}

export function checkColumnAction({
  dispatch,
  columnKey,
  columnsOrder,
}: {
  dispatch: React.Dispatch<TsalesMatchingDispatchReducer>;
  columnKey: string;
  columnsOrder: Record<string, ITableColumnsSelectOrderState>;
}) {
  dispatch({
    type: types.SET_COLUMNS_ORDER,
    payload: {
      ...columnsOrder,
      [columnKey]: {
        ...columnsOrder[columnKey],
        show: !columnsOrder[columnKey].show,
      },
    },
  });
}

/////////////////////////////////////////////////////
/////////////////////////////////////////////////////
export async function getCatalogData({
  dispatch,
  salesMatchingBodyFetch,
  signal,
  token,
}: {
  dispatch: React.Dispatch<TsalesMatchingDispatchReducer>;
  salesMatchingBodyFetch: IgetSalesMatchingAPIBody;
  signal: AbortSignal;
  token: string;
}): Promise<boolean> {
  let values: IgetSalesMatchingData = {
    count: 0,
    total: 0,
    data: [] as Array<IgetSalesMatchingDataItem>,
  };
  try {
    dispatch({ type: types.SET_IS_LOADING_CATALOG, payload: true });
    const catalogFetch = await getSalesMatchingItemAPI(
      salesMatchingBodyFetch,
      signal,
      token
    );

    if (catalogFetch.response.status === 200) {
      for (const item of catalogFetch.data.data) {
        const transformItem: IgetSalesMatchingDataItem = {
          ...item,
          key: `${item.id}`,
        };
        values.data.push(transformItem);
      }
      values.count = catalogFetch.data.count;
      values.total = catalogFetch.data.total;
      dispatch({ type: types.SET_CATALOG_ITEMS, payload: values ?? [] });
      return true;
    }
    if (catalogFetch.response.status === 404) return true;
    return false;
  } catch (err) {
    return false;
  } finally {
    dispatch({ type: types.SET_IS_LOADING_CATALOG, payload: false });
  }
}
